import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../components/PageHeader.js';
import { useTenantContext } from '../api/tenant-context.js';
import { listChannels } from '../api/channels.js';
import { listConnectors } from '../api/connectors.js';
import { listCampaigns } from '../api/campaigns.js';
import { getDashboardKpis } from '../api/reporting.js';

/**
 * #157 — Customer Dashboard real-time + Live Ops.
 *
 * Paridad con admin-spa TenantDashboard (#155): consume /dashboard/kpis del
 * reporting-api con polling 30s. Reemplaza las métricas mock por KPIs reales
 * (msgs_received, flows_completed, handoffs, outbound, surveys, nps_avg),
 * agrega chats activos/queue + word cloud.
 */

export function Dashboard(): JSX.Element {
  const { tenant } = useTenantContext();
  const tenantId = tenant?.id ?? '';

  const channelsQ = useQuery({
    queryKey: ['channels', tenantId],
    queryFn: () => listChannels(tenantId),
    enabled: !!tenantId,
  });
  const connectorsQ = useQuery({
    queryKey: ['connectors', tenantId],
    queryFn: () => listConnectors(tenantId),
    enabled: !!tenantId,
  });
  const campaignsQ = useQuery({
    queryKey: ['campaigns', tenantId],
    queryFn: () => listCampaigns(tenantId),
    enabled: !!tenantId,
  });
  const kpisQ = useQuery({
    queryKey: ['dashboard-kpis', tenantId],
    queryFn: () => getDashboardKpis(25),
    enabled: !!tenantId,
    refetchInterval: 30_000,
  });

  const channels = channelsQ.data ?? [];
  const connectors = connectorsQ.data ?? [];
  const campaigns = campaignsQ.data ?? [];
  const kpis = kpisQ.data?.metrics;
  const activeChats = kpisQ.data?.activeChats ?? [];
  const queueChats = kpisQ.data?.queueChats ?? [];
  const wordCloud = kpisQ.data?.wordCloud ?? [];

  const activeCampaigns = campaigns.filter((c) => c.status === 'running' || c.status === 'scheduled');
  const allOk = channels.every((c) => c.status === 'connected') && connectors.every((c) => c.status === 'connected');
  const hasErrors = (kpis?.outbound_failed ?? 0) > 0;

  const lastSync = kpisQ.data?.generatedAt
    ? new Date(kpisQ.data.generatedAt).toLocaleTimeString()
    : '—';

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Estado real-time de tu plataforma conversacional."
        actions={
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => {
              channelsQ.refetch(); connectorsQ.refetch();
              campaignsQ.refetch(); kpisQ.refetch();
            }}
          >
            Refrescar
          </button>
        }
      />

      {/* Hero status */}
      <div
        className="flex items-center gap-4 p-5 rounded-[12px] mb-5"
        style={{
          background: allOk && !hasErrors
            ? 'linear-gradient(135deg, rgba(34,197,94,0.10), rgba(34,197,94,0.02))'
            : 'var(--surface)',
          border: `1px solid ${allOk && !hasErrors ? 'rgba(34,197,94,0.25)' : 'var(--border)'}`,
        }}
      >
        <div
          className="w-11 h-11 rounded-[10px] flex items-center justify-center shrink-0"
          style={{
            background: allOk && !hasErrors ? 'var(--ok-soft)' : 'var(--surface-2)',
            color: allOk && !hasErrors ? 'var(--ok)' : 'var(--warn)',
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <div className="flex-1">
          <div className="text-base font-semibold">
            {allOk && !hasErrors
              ? 'Todo está funcionando correctamente'
              : hasErrors ? 'Hay envíos fallidos recientes' : 'Hay conexiones que requieren atención'}
          </div>
          <div className="text-[13px] text-ink-3 mt-0.5">
            {channels.length + connectors.length} conexiones · {activeCampaigns.length} campañas activas · {activeChats.length} chats activos · {queueChats.length} en cola
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11.5px] uppercase tracking-wider text-ink-4 mb-0.5">
            Última sincronización
          </div>
          <strong className="mono">{lastSync}</strong>
        </div>
      </div>

      {/* KPIs reales */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-5">
        <Kpi label="Mensajes recibidos" value={fmtNum(kpis?.msgs_received ?? 0)} meta="24h" />
        <Kpi label="Flujos completados" value={fmtNum(kpis?.flows_completed ?? 0)} meta="24h" />
        <Kpi label="Handoffs solicitados" value={fmtNum(kpis?.handoffs_requested ?? 0)} meta="a agentes" />
        <Kpi label="Outbound enviados" value={fmtNum(kpis?.outbound_sent ?? 0)} meta="24h" />
        <Kpi label="Outbound fallidos" value={fmtNum(kpis?.outbound_failed ?? 0)}
          meta={hasErrors ? 'requiere atención' : 'sin incidentes'}
          warn={hasErrors} />
        <Kpi label="Encuestas iniciadas" value={fmtNum(kpis?.surveys_started ?? 0)} meta="24h" />
        <Kpi label="Encuestas completadas" value={fmtNum(kpis?.surveys_completed ?? 0)} meta="24h" />
        <Kpi
          label="NPS promedio"
          value={kpis?.nps_avg !== null && kpis?.nps_avg !== undefined ? kpis.nps_avg.toFixed(1) : '—'}
          meta="últimas encuestas"
        />
      </div>

      {/* Live ops: activos + queue + cloud */}
      <div className="grid lg:grid-cols-[1fr_1fr] gap-5 mb-5">
        <div className="panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[14px] font-semibold">Chats activos · {activeChats.length}</div>
              <div className="text-[12px] text-ink-3 mt-0.5">Conversaciones en flujo</div>
            </div>
          </div>
          {activeChats.length === 0 ? (
            <div className="text-ink-3 text-sm">Sin chats activos.</div>
          ) : (
            <div className="flex flex-col gap-2 max-h-80 overflow-y-auto">
              {activeChats.map((c) => (
                <ChatRow key={c.conversationId} c={c} />
              ))}
            </div>
          )}
        </div>

        <div className="panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[14px] font-semibold">En cola · {queueChats.length}</div>
              <div className="text-[12px] text-ink-3 mt-0.5">Esperando atención de agente</div>
            </div>
          </div>
          {queueChats.length === 0 ? (
            <div className="text-ink-3 text-sm">Sin chats en cola.</div>
          ) : (
            <div className="flex flex-col gap-2 max-h-80 overflow-y-auto">
              {queueChats.map((c) => (
                <ChatRow key={c.conversationId} c={c} />
              ))}
            </div>
          )}
        </div>
      </div>

      {wordCloud.length > 0 && (
        <div className="panel mb-5">
          <div className="text-[14px] font-semibold mb-3">
            Palabras frecuentes ({wordCloud.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {wordCloud.slice(0, 50).map((w) => {
              const size = 11 + Math.min(14, Math.floor(Math.log2(1 + w.count) * 2));
              return (
                <span
                  key={w.word}
                  className="rounded-[6px] px-2 py-0.5"
                  style={{
                    fontSize: `${size}px`,
                    background: 'var(--bg-2)',
                    border: '1px solid var(--border)',
                  }}
                  title={`${w.count} ocurrencias`}
                >
                  {w.word}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Conexiones */}
      <div className="panel">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[14px] font-semibold">Estado de conexiones</div>
            <div className="text-[12px] text-ink-3 mt-0.5">Channels + connectors del tenant</div>
          </div>
        </div>
        {channelsQ.isLoading || connectorsQ.isLoading ? (
          <div className="text-ink-3 text-sm">Cargando…</div>
        ) : channels.length + connectors.length === 0 ? (
          <div className="text-ink-3 text-sm">No hay conexiones registradas.</div>
        ) : (
          <div className="flex flex-col gap-2">
            {channels.map((c) => (
              <ConnRow key={`ch-${c.id}`} kind="Channel" name={c.name}
                meta={c.displayPhoneNumber ?? c.channelType} status={c.status} />
            ))}
            {connectors.map((c) => (
              <ConnRow key={`co-${c.id}`} kind="Connector" name={c.name}
                meta={c.provider} status={c.status} />
            ))}
          </div>
        )}
      </div>

      {kpisQ.data?.note && (
        <div className="mt-4 text-[11px] text-ink-4 italic">
          {kpisQ.data.note}
        </div>
      )}
    </>
  );
}

function fmtNum(v: number): string {
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return String(Math.round(v));
}

function Kpi({ label, value, meta, warn }: {
  label: string; value: string; meta: string; warn?: boolean;
}): JSX.Element {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={warn ? { color: 'var(--warn)' } : undefined}>{value}</div>
      <div className="kpi-meta">{meta}</div>
    </div>
  );
}

function ChatRow({ c }: { c: { conversationId: string; channel: string; flowId: string; updatedAt: string; status: string } }): JSX.Element {
  const ago = formatAgo(c.updatedAt);
  return (
    <div
      className="flex items-center gap-3 px-3 py-2 rounded-[8px]"
      style={{ background: 'var(--bg-2)', border: '1px solid var(--border)' }}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] font-mono truncate">{c.conversationId.slice(0, 20)}…</div>
        <div className="text-[11px] text-ink-3 truncate">
          {c.channel} · flow {c.flowId.slice(0, 10)}
        </div>
      </div>
      <div className="text-[11px] text-ink-3">{ago}</div>
    </div>
  );
}

function ConnRow({ kind, name, meta, status }: {
  kind: string; name: string; meta: string; status: string;
}): JSX.Element {
  const ok = status === 'connected';
  return (
    <div
      className="flex items-center gap-3 px-3 py-2.5 rounded-[8px]"
      style={{ background: 'var(--bg-2)', border: '1px solid var(--border)' }}
    >
      <div className="text-[11px] uppercase tracking-wider text-ink-4 w-[80px] shrink-0">{kind}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-medium truncate">{name}</div>
        <div className="text-[12px] text-ink-3 mono truncate">{meta}</div>
      </div>
      <div className="flex items-center gap-2">
        <span className={`dot ${ok ? 'dot-ok' : 'dot-err'}`} />
        <span style={{ color: ok ? 'var(--ok)' : 'var(--err)' }} className="text-[12.5px]">
          {ok ? 'Operativo' : status}
        </span>
      </div>
    </div>
  );
}

function formatAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h`;
}
